import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Plus, Loader2, Eye, Search, Filter, X, RefreshCw } from 'lucide-react';
import { getInventoryRules, createInventoryRule, getInventoryRuleById, getCustomKeysFromGAM, type InventoryRulesFilters } from '@/services/api/inventory-rules';
import type { InventoryRule, CreateInventoryRuleInput } from '@/types/inventory-rule';
import { InventoryRuleStatus } from '@/types/inventory-rule';
import { useNetwork } from '@/contexts/NetworkContext';

export default function InventoryRules() {
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
    const [selectedRule, setSelectedRule] = useState<InventoryRule | null>(null);
    const [formData, setFormData] = useState<Omit<CreateInventoryRuleInput, 'status' | 'network'>>({
        custom_key: '',
        custom_value: '',
    });
    const [selectedNetworkId, setSelectedNetworkId] = useState<string>('');
    const [showFilters, setShowFilters] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState<InventoryRulesFilters>({
        search: '',
        status: '',
    });

    const { toast } = useToast();
    const { networks } = useNetwork();
    const queryClient = useQueryClient();

    // Preparar filtros para a API (remover campos vazios)
    const apiFilters: InventoryRulesFilters = {};
    if (searchTerm?.trim()) {
        apiFilters.search = searchTerm.trim();
    }
    if (filters.status) {
        apiFilters.status = filters.status;
    }

    // Query para listar regras de inventário com filtros
    const { data: rules, isLoading, error } = useQuery({
        queryKey: ['inventory-rules', searchTerm, filters.status],
        queryFn: () => getInventoryRules(Object.keys(apiFilters).length > 0 ? apiFilters : undefined),
    });

    // Query para buscar custom keys do GAM quando um network é selecionado
    const { data: customKeys, isLoading: keysLoading } = useQuery({
        queryKey: ['custom-keys', selectedNetworkId],
        queryFn: () => getCustomKeysFromGAM(selectedNetworkId),
        enabled: !!selectedNetworkId && isCreateDialogOpen,
    });

    // Mutation para criar regra de inventário
    const createRuleMutation = useMutation({
        mutationFn: createInventoryRule,
        onSuccess: () => {
            toast({
                title: 'Regra criada com sucesso!',
                description: 'A regra de inventário foi criada e está aguardando processamento.',
            });
            queryClient.invalidateQueries({ queryKey: ['inventory-rules'] });
            setIsCreateDialogOpen(false);
            resetForm();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar regra',
                description: error.response?.data?.detail || 'Ocorreu um erro ao criar a regra de inventário.',
            });
        },
    });

    // Query para obter detalhes de uma regra específica
    const { data: ruleDetails, isLoading: detailsLoading } = useQuery({
        queryKey: ['inventory-rule', selectedRule?.id],
        queryFn: () => getInventoryRuleById(selectedRule!.id),
        enabled: !!selectedRule && isViewDialogOpen,
    });

    // Mutation para sync
    const syncMutation = useMutation({
        mutationFn: async () => {
            const response = await fetch(`${import.meta.env.VITE_GAM_API_URL}/sync/inventory-rules`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Erro ao sincronizar');
            }
            return response.json();
        },
        onSuccess: () => {
            toast({
                title: 'Sincronização concluída!',
                description: 'As regras de inventário foram sincronizadas com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['inventory-rules'] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao sincronizar',
                description: error.message || 'Ocorreu um erro ao sincronizar as regras de inventário.',
            });
        },
    });

    const handleSync = () => {
        syncMutation.mutate();
    };

    const resetForm = () => {
        setFormData({ custom_key: '', custom_value: '' });
        setSelectedNetworkId('');
    };

    const handleCreateSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedNetworkId.trim()) {
            toast({
                variant: 'destructive',
                title: 'Campo obrigatório',
                description: 'Por favor, selecione a Conta GAM.',
            });
            return;
        }

        if (!formData.custom_key.trim()) {
            toast({
                variant: 'destructive',
                title: 'Campo obrigatório',
                description: 'Por favor, selecione a Custom Key.',
            });
            return;
        }

        if (!formData.custom_value.trim()) {
            toast({
                variant: 'destructive',
                title: 'Campo obrigatório',
                description: 'Por favor, preencha o Custom Value.',
            });
            return;
        }

        // Sempre envia status como pending
        const createData: CreateInventoryRuleInput = {
            ...formData,
            network: selectedNetworkId,
            status: InventoryRuleStatus.PENDING,
        };

        createRuleMutation.mutate(createData);
    };

    const openViewDialog = (rule: InventoryRule) => {
        setSelectedRule(rule);
        setIsViewDialogOpen(true);
    };

    const getStatusBadge = (status: InventoryRuleStatus) => {
        const statusConfig = {
            [InventoryRuleStatus.PENDING]: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
            [InventoryRuleStatus.PROCESSING]: { label: 'Processando', className: 'bg-blue-100 text-blue-800 border-blue-300' },
            [InventoryRuleStatus.COMPLETED]: { label: 'Concluído', className: 'bg-green-100 text-green-800 border-green-300' },
            [InventoryRuleStatus.FAILED]: { label: 'Falhou', className: 'bg-red-100 text-red-800 border-red-300' },
        };

        const config = statusConfig[status];
        return (
            <Badge variant="outline" className={config.className}>
                {config.label}
            </Badge>
        );
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Regras de Inventário</h1>
                    <p className="mt-2 text-gray-600">Gerencie as regras de inventário do Google Ad Manager.</p>
                </div>
                <div className="flex gap-2">
                    <Button 
                        onClick={handleSync}
                        disabled={syncMutation.isPending}
                        variant="outline"
                        className="border-blue-600 text-blue-600 hover:bg-blue-50"
                    >
                        {syncMutation.isPending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Sincronizando...
                            </>
                        ) : (
                            <>
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Sync
                            </>
                        )}
                    </Button>
                    <Button 
                        onClick={() => setIsCreateDialogOpen(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Nova Regra
                    </Button>
                </div>
            </div>

            {/* Filters Bar */}
            <div className="rounded-lg bg-white shadow border border-gray-100 p-4">
                <div className="flex items-center gap-4">
                    <Button
                        variant="outline"
                        onClick={() => setShowFilters(!showFilters)}
                        className="flex items-center gap-2"
                    >
                        <Filter className="h-4 w-4" />
                        Filtros
                        {(searchTerm || filters.status) && (
                            <Badge variant="secondary" className="ml-1">
                                {[searchTerm, filters.status].filter(Boolean).length}
                            </Badge>
                        )}
                    </Button>

                    {(searchTerm || filters.status) && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setSearchTerm('');
                                setFilters({ search: '', status: '' });
                            }}
                            className="text-red-600 hover:text-red-700"
                        >
                            <X className="h-4 w-4 mr-1" />
                            Limpar filtros
                        </Button>
                    )}
                </div>

                {showFilters && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                        <div className="space-y-2">
                            <Label htmlFor="search">Buscar</Label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                    id="search"
                                    type="text"
                                    placeholder="Buscar por custom key ou custom value..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                            <p className="text-xs text-gray-500">
                                Busca em ambos os campos (custom key e custom value)
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="filter-status">Status</Label>
                            <Select
                                value={filters.status || undefined}
                                onValueChange={(value) =>
                                    setFilters({ ...filters, status: value || '' })
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Todos os status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={InventoryRuleStatus.PENDING}>Pendente</SelectItem>
                                    <SelectItem value={InventoryRuleStatus.PROCESSING}>Processando</SelectItem>
                                    <SelectItem value={InventoryRuleStatus.COMPLETED}>Concluído</SelectItem>
                                    <SelectItem value={InventoryRuleStatus.FAILED}>Falhou</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                )}
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className="rounded-lg bg-white p-6 shadow flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    <p className="ml-2 text-gray-600">Carregando regras...</p>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-6">
                    <p className="text-red-800">Erro ao carregar regras. Tente novamente.</p>
                </div>
            )}

            {/* Rules Table */}
            {rules && rules.length > 0 && (
                <div className="rounded-lg bg-white shadow border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Custom Key
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Custom Value
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Mensagem de Erro
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Ações
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {rules.map((rule) => (
                                    <tr key={rule.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <p className="text-sm font-medium text-gray-900">{rule.id}</p>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <p className="text-sm text-gray-900">{rule.custom_key}</p>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <p className="text-sm text-gray-900">{rule.custom_value}</p>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getStatusBadge(rule.status)}
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="text-sm text-gray-900 max-w-md truncate">
                                                {rule.error_message || '-'}
                                            </p>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => openViewDialog(rule)}
                                            >
                                                <Eye className="h-4 w-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Empty State */}
            {rules && rules.length === 0 && (
                <div className="rounded-lg bg-white p-12 shadow border border-gray-100 text-center">
                    <Plus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {(searchTerm || filters.status)
                            ? 'Nenhuma regra encontrada'
                            : 'Nenhuma regra cadastrada'}
                    </h3>
                    <p className="text-gray-600 mb-4">
                        {(searchTerm || filters.status)
                            ? 'Tente ajustar os filtros de busca.'
                            : 'Comece criando sua primeira regra de inventário.'}
                    </p>
                    {(searchTerm || filters.status) ? (
                        <Button
                            variant="outline"
                            onClick={() => {
                                setSearchTerm('');
                                setFilters({ search: '', status: '' });
                            }}
                        >
                            <X className="mr-2 h-4 w-4" />
                            Limpar Filtros
                        </Button>
                    ) : (
                        <Button onClick={() => setIsCreateDialogOpen(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            Nova Regra
                        </Button>
                    )}
                </div>
            )}

            {/* Create Rule Dialog */}
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Criar Nova Regra de Inventário</DialogTitle>
                        <DialogDescription>
                            Adicione uma nova regra de inventário para o Google Ad Manager.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleCreateSubmit} className="space-y-4 mt-4">
                        <div className="space-y-2">
                            <Label htmlFor="network">Conta GAM *</Label>
                            <Select
                                value={selectedNetworkId}
                                onValueChange={(value) => {
                                    setSelectedNetworkId(value);
                                    setFormData({ ...formData, custom_key: '' });
                                }}
                                disabled={createRuleMutation.isPending}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Selecione a Conta GAM para buscar as Custom Keys" />
                                </SelectTrigger>
                                <SelectContent>
                                    {networks.map((network) => (
                                        <SelectItem key={network.network_code} value={network.network_code}>
                                            {network.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500">
                                Selecione a Conta GAM para carregar as Custom Keys disponíveis do GAM
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="custom_key">Custom Key *</Label>
                            <Select
                                value={formData.custom_key}
                                onValueChange={(value) =>
                                    setFormData({ ...formData, custom_key: value })
                                }
                                disabled={createRuleMutation.isPending || !selectedNetworkId || keysLoading}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={keysLoading ? "Carregando..." : selectedNetworkId ? "Selecione a Custom Key" : "Selecione primeiro a Conta GAM"} />
                                </SelectTrigger>
                                <SelectContent>
                                    {customKeys?.map((keyName) => (
                                        <SelectItem key={keyName} value={keyName}>
                                            {keyName}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {keysLoading && (
                                <p className="text-xs text-gray-500">Carregando custom keys...</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="custom_value">Custom Value *</Label>
                            <Input
                                id="custom_value"
                                type="text"
                                placeholder="Ex: mobile, BR, etc."
                                value={formData.custom_value}
                                onChange={(e) =>
                                    setFormData({ ...formData, custom_value: e.target.value.trim() })
                                }
                                disabled={createRuleMutation.isPending}
                                required
                            />
                        </div>

                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setIsCreateDialogOpen(false);
                                    resetForm();
                                }}
                                disabled={createRuleMutation.isPending}
                                className="flex-1"
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" disabled={createRuleMutation.isPending} className="flex-1">
                                {createRuleMutation.isPending ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Criando...
                                    </>
                                ) : (
                                    'Criar Regra'
                                )}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* View Rule Dialog */}
            <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Detalhes da Regra de Inventário</DialogTitle>
                        <DialogDescription>
                            Informações completas da regra de inventário.
                        </DialogDescription>
                    </DialogHeader>

                    {detailsLoading ? (
                        <div className="flex items-center justify-center p-8">
                            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                            <p className="ml-2 text-gray-600">Carregando detalhes...</p>
                        </div>
                    ) : ruleDetails ? (
                        <div className="mt-4 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-sm font-medium text-gray-500">ID</Label>
                                    <p className="mt-1 text-sm text-gray-900">{ruleDetails.id}</p>
                                </div>
                                <div>
                                    <Label className="text-sm font-medium text-gray-500">Status</Label>
                                    <div className="mt-1">
                                        {getStatusBadge(ruleDetails.status)}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <Label className="text-sm font-medium text-gray-500">Custom Key</Label>
                                <p className="mt-1 text-sm text-gray-900">{ruleDetails.custom_key}</p>
                            </div>

                            <div>
                                <Label className="text-sm font-medium text-gray-500">Custom Value</Label>
                                <p className="mt-1 text-sm text-gray-900">{ruleDetails.custom_value}</p>
                            </div>

                            {ruleDetails.error_message && (
                                <div>
                                    <Label className="text-sm font-medium text-gray-500">Mensagem de Erro</Label>
                                    <p className="mt-1 text-sm text-red-600">{ruleDetails.error_message}</p>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-sm font-medium text-gray-500">Criado em</Label>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {ruleDetails.created_at 
                                            ? new Date(ruleDetails.created_at).toLocaleString('pt-BR')
                                            : '-'}
                                    </p>
                                </div>
                                <div>
                                    <Label className="text-sm font-medium text-gray-500">Atualizado em</Label>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {ruleDetails.updated_at 
                                            ? new Date(ruleDetails.updated_at).toLocaleString('pt-BR')
                                            : '-'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    <div className="flex gap-3 pt-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setIsViewDialogOpen(false);
                                setSelectedRule(null);
                            }}
                            className="flex-1"
                        >
                            Fechar
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

